import { useState, type FormEvent } from "react"
import type { AnswerHead, MatterContext } from "../contract"
import { Lean } from "../ds"
import { stateName, sideWords, sourceWords, type ThreadContext } from "../readerContext"
import { assumption, contextParts, splitShort } from "./format"

interface AnswerCardProps {
  id: string
  head: AnswerHead
  /** Sends a new turn with `answers.context` set to the edited matter details. */
  onContext: (ctx: MatterContext) => void
  /** "3 decisions help, 1 binds against you." from the decisions blocks; empty when none. */
  weighing?: string
  /** Who the answer was written for, for the meta line. */
  reader?: ThreadContext
  /** A shared copy: the matter chips show, without Edit. */
  readOnly?: boolean
  /** A turn is running: Update waits (a second send would be refused as busy). */
  busy?: boolean
}

const num = (v: FormDataEntryValue | null) => {
  const n = parseFloat(String(v ?? ""))
  return Number.isFinite(n) && n > 0 ? n : null
}
const text = (v: FormDataEntryValue | null) => {
  const s = String(v ?? "").trim()
  return s ? s : null
}

/**
 * Where you stand (answer anatomy 2): the verdict in serif, the lead under it,
 * then the matter as chips with Edit. Assumptions are chips too, marked as assumed.
 */
export function AnswerCard({ id, head, onContext, weighing = "", reader, readOnly, busy }: AnswerCardProps) {
  const [editing, setEditing] = useState(false)
  const [a, u, z] = splitShort(head)
  const parts = contextParts(head.context)
  const assumed = (head.assumptions ?? []).map(assumption).filter(Boolean)
  const ctx = head.context ?? {}

  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (busy) return
    const f = new FormData(e.currentTarget)
    const lakhs = num(f.get("amount"))
    setEditing(false)
    onContext({
      state: text(f.get("state")),
      period: text(f.get("period")),
      section: text(f.get("section")),
      notice_date: text(f.get("notice_date")),
      amount_inr: lakhs === null ? null : Math.round(lakhs * 1e5),
    })
  }

  return (
    <article className="averdict" id={id} aria-label="Where you stand">
      {head.lean ? (
        <div className="vlean">
          <Lean lean={head.lean.toward} label={null} size="lg" className="vlean-glyph" />
          <p>
            <strong>{head.lean.line}</strong>
            {weighing ? ` ${weighing}` : ""}
          </p>
        </div>
      ) : null}
      <h2 className="verdict">
        {a}
        {u ? <span className="ul">{u}</span> : null}
        {z}
      </h2>
      {head.lede ? <p className="vlead">{head.lede}</p> : null}
      {reader ? (
        <p className="vmeta">
          Answered {sideWords(reader.side)} in {reader.state || stateName(reader.state_code)}, law as of today.
          {reader.state_source && reader.state_source !== "chosen" && reader.state_source !== "none" ? ` ${sourceWords(reader)}` : ""}
        </p>
      ) : null}
      {parts.length || assumed.length ? (
        <div className="vchips" aria-label="The matter as Nira read it">
          {parts.map((p) => (
            <span className="vchip" key={p}>{p}</span>
          ))}
          {readOnly ? null : (
            <button type="button" className="vedit" aria-expanded={editing} onClick={() => setEditing((v) => !v)}>
              Edit
            </button>
          )}
        </div>
      ) : null}
      {assumed.length ? (
        <div className="vasm">
          <p className="lab">Nira assumed</p>
          <ul>
            {assumed.map((a) => (
              <li key={a}>
                {readOnly ? <span>{a}</span> : <button type="button" onClick={() => setEditing(true)} title="Tell Nira if this is wrong">{a}</button>}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {editing && !readOnly ? (
        <form className="edit" onSubmit={submit} aria-label="Edit matter details">
          <label><span className="lab">Notice date</span><input type="date" name="notice_date" defaultValue={ctx.notice_date ?? ""} autoFocus /></label>
          <label>
            <span className="lab">Amount (₹ lakh)</span>
            <input type="number" name="amount" min="0.1" step="0.1" defaultValue={ctx.amount_inr ? String(ctx.amount_inr / 1e5) : ""} />
          </label>
          <label><span className="lab">State</span><input type="text" name="state" defaultValue={ctx.state ?? ""} /></label>
          <label><span className="lab">Period</span><input type="text" name="period" defaultValue={ctx.period ?? ""} /></label>
          <label><span className="lab">Section</span><input type="text" name="section" defaultValue={ctx.section ?? ""} /></label>
          <button className="vupdate" type="submit" disabled={busy}>Update</button>
        </form>
      ) : null}
    </article>
  )
}
